import React from "react";

/**
 * ErrorBoundary — contains a render/runtime error in its subtree so one bad record
 * degrades to a small "couldn't load" panel instead of white-screening the whole app.
 *
 * Motivating case: the 2026-07-07 export-v2 merge inserted records via raw insertMany,
 * which bypasses Mongoose's `default: []`, so many records have array fields ABSENT
 * (not []). Unguarded reads like `client.documents.length` (ClientDatabase) then throw
 * mid-.map() and, with ZERO error boundaries previously in the tree, blank the entire
 * view. This wraps each major view so the blast radius is one panel, not the page.
 * See REFACTOR-READINESS.md.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Log the error + React component stack for support/debugging. No PII — just the
    // message and the component path (never the record that triggered it).
    console.error(
      `ErrorBoundary caught an error in "${this.props.label || "a section"}":`,
      error && error.message,
      info && info.componentStack
    );
  }

  handleRetry = () => {
    // Best-effort: clears the error so the subtree re-mounts. If the underlying data is
    // still malformed it will re-throw and be caught again — that's fine, the page stays up.
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      const label = this.props.label || "this section";
      return (
        <div
          className="card"
          role="alert"
          style={{
            margin: "10px",
            padding: "16px",
            textAlign: "center",
            backgroundColor: "rgba(255,255,255,0.85)",
            color: "black"
          }}
        >
          <h5 style={{ marginBottom: "8px" }}>Couldn&apos;t load {label}.</h5>
          <p style={{ marginBottom: "12px" }}>
            Something went wrong displaying this section. The rest of the page is unaffected.
          </p>
          <button
            className="btn btn-sm btn-secondary"
            style={{ marginRight: "8px" }}
            onClick={this.handleRetry}
          >
            Try again
          </button>
          <button className="btn btn-sm btn-light" onClick={() => window.location.reload()}>
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
